/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MarketoFormWrapper from "@stoneage/App/Components/MarketoFormModal/MarketoFormWrapper";
import { CustomAppState, xMarketoModalFormsActions } from "@stoneage/App/Store/Reducers";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaRfqConfigureButtonClickEvent,
    trackGaRfqFooterEvent,
} from "@stoneage/App/Utilities/DataLayer";
import { constants } from "@stoneage/XDL/constants";
import { marketoDefaultCss } from "@stoneage/XDL/Utilities/SharedStyles/sharedStyles";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css } from "styled-components";
// eslint-disable-next-line spire/fenced-imports
import { hideAll as hideAllTippyInstances } from "tippy.js";

type Props = WidgetProps &
    HasProduct &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    DispatchProp<xMarketoModalFormsActions> &
    HasHistory &
    HasShellContext;
export interface UserData {
    isAuthenticated: boolean;
    currentCurrency?: string;
    currentLanguage?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    country?: string;
    state?: string;
    company?: string;
}

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    totalQuantity: state.xProductBomAction.totalQuantity,
    customerNumber: getBillToCustomerNumber(state),
    impersonation: trackAsImpersonation(state),
    checkedPartNumbers: state.xProductBomAction.checkedPartNumbers,
    checkedProducts: state.xProductBomAction.checkedProducts,
    isBomView: (state as CustomAppState).xCommon.isBomView,
    bomRequestQuoteFooterEvent: (state as CustomAppState).xGoogleAnalytics.bomRequestQuoteFooterEvent,
    bomConfigureOrRequestQuoteClickEvent: (state as CustomAppState).xGoogleAnalytics
        .bomConfigureOrRequestQuoteClickEvent,
});

const mapDispatchToProps = {};

export interface ContactUsForQuoteButtonStyles {
    button?: ButtonPresentationProps;
    unauthenticatedMessage?: TypographyProps;
    whiteContainer?: InjectableCss;
    message?: TypographyProps;
    viewLink?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const contactUsForQuoteButtonStyles: ContactUsForQuoteButtonStyles = {
    button: {
        typographyProps: {
            css: css`
                &&& {
                    text-transform: none;
                    font-weight: 500;
                }
            `,
        },
        css: css`
            width: 100%;
            justify-content: center;

            &&& svg {
                stroke-width: 3px;
            }
        `,
    },
    unauthenticatedMessage: {
        size: "12px",
        lineHeight: "18px",
        css: css`
            display: block;
            margin-bottom: 12px;
            @media print {
                display: none;
            }
        `,
    },
    whiteContainer: {
        css: css`
            margin-top: 12px;
            background: white;
            padding: 8px;
            &&:nth-child(2n) {
                margin-top: 20px;
            }
            @media print {
                display: none;
            }
        `,
    },
    message: {
        size: "12px",
        lineHeight: "18px",
        css: css`
            margin-bottom: 8px;
            display: block;
        `,
    },
    viewLink: {
        typographyProps: {
            size: 12,
            weight: "bold",
        },
        icon: {
            iconProps: {
                src: "IconArrowRightCopper",
                size: 9,
            },
            position: "right",
        },
        color: "secondary",
        css: css`
            text-transform: capitalize;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = contactUsForQuoteButtonStyles;

const ContactUsForQuoteButton = ({
    totalQuantity,
    product,
    fields: {
        formDescription,
        buttonText,
        height,
        width,
        displayAsButton,
        formId,
        instructionMessage,
        customCss,
        isMultipleProducts,
        displayMessage,
    },
    customerNumber,
    impersonation,
    checkedPartNumbers,
    isBomView,
    bomRequestQuoteFooterEvent,
    bomConfigureOrRequestQuoteClickEvent,
    checkedProducts,
}: Props) => {
    const [showModal, setShowModal] = React.useState(false);
    const clickHandler = () => {
        let erps = "";
        if (isMultipleProducts) {
            // store the partnumbers separated by comma, with quantity as (erpNumber:quantity)
            Object.values(checkedProducts).forEach(product => {
                erps += `${(product as any).erpNumber}:${product.qtyOrdered},`;
            });
            erps += `Total:${Object.keys(checkedPartNumbers)?.length}`;

            // track GA4 event
            bomRequestQuoteFooterEvent && trackGaRfqFooterEvent(erps, buttonText, impersonation, customerNumber);
        } else if (isBomView) {
            bomConfigureOrRequestQuoteClickEvent &&
                trackGaRfqConfigureButtonClickEvent(product.productNumber, buttonText, impersonation, customerNumber);
        }
        // isMultipleProducts means its coming from iBOM, else the partNumber is already stored in localstorage when accessing pdp
        if (isMultipleProducts && erps) {
            localStorage && localStorage.setItem("productPartNumber", erps);
        }
        setShowModal(true);
        hideAllTippyInstances();
    };

    if (product.cantBuy && !isMultipleProducts) {
        return <Typography {...styles.unauthenticatedMessage}>{siteMessage("Custom_PDP_CantQuoteMessage")}</Typography>;
    }

    return (
        <StyledWrapper
            css={css`
                @media print {
                    display: none;
                }
            `}
        >
            {!displayAsButton ? (
                <StyledWrapper {...styles.whiteContainer}>
                    {displayMessage && (
                        <Typography className="contact-for-quote-msg" {...styles.unauthenticatedMessage}>
                            {siteMessage("Custom_PDP_RequestQuoteMessage")}
                        </Typography>
                    )}

                    <Link disabled={isMultipleProducts && !totalQuantity} onClick={clickHandler} {...styles.viewLink}>
                        {buttonText}
                    </Link>
                </StyledWrapper>
            ) : (
                <StyledWrapper
                    css={css`
                        width: 100%;
                    `}
                >
                    {displayMessage && (
                        <Typography className="contact-for-quote-msg" {...styles.unauthenticatedMessage}>
                            {siteMessage("Custom_PDP_RequestQuoteMessage")}
                        </Typography>
                    )}
                    <Button disabled={isMultipleProducts && !totalQuantity} onClick={clickHandler} {...styles.button}>
                        {buttonText}
                    </Button>
                </StyledWrapper>
            )}

            <Modal
                size={width || 700}
                headline={translate(buttonText)}
                isOpen={showModal}
                handleClose={() => setShowModal(false)}
            >
                {formDescription && (
                    <Typography
                        css={css`
                            display: block;
                        `}
                    >
                        {parse(formDescription, parserOptions)}
                    </Typography>
                )}

                <MarketoFormWrapper
                    formWidth={width}
                    formHeight={height}
                    confirmationMessage={instructionMessage}
                    formId={formId}
                    customCss={customCss}
                    inModal={false}
                    useRenderFunction={true}
                    formType="requestQuote"
                    pdpItem={product?.productTitle}
                />
            </Modal>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advnacedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: withProduct(
        connect(mapStateToProps, mapDispatchToProps)(withHistory(withIsInShell(ContactUsForQuoteButton))),
    ),
    definition: {
        displayName: "Quote Button",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: "displayAsButton",
                displayName: "Display as Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: "height",
                displayName: "Form Height in Pixels",
                editorTemplate: "IntegerField",
                defaultValue: constants.marketoModalDefaultDimensions.height,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "width",
                displayName: "Form Width in Pixels",
                editorTemplate: "IntegerField",
                defaultValue: constants.marketoModalDefaultDimensions.width,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "formDescription",
                displayName: "Body Copy",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "formId",
                displayName: "Form ID",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "instructionMessage",
                displayName: "Success Message Copy",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: "displayMessage",
                displayName: "Message",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isVisible: item => false,
            },
            {
                name: "isMultipleProducts",
                displayName: "multiple products",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => false,
                tab: basicTab,
            },
            {
                name: "customCss",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: marketoDefaultCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
                tab: advnacedTab,
            },
        ],
    },
};

export default widgetModule;
